import { KitButton, KitText } from '@omni/kit/components';
import KitEventIcon from '@omni/kit/components/KitEventIcon';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import { IEvent } from '@omni/kit/services/EventsService/Types';
import { IGroupEvent } from '@omni/kit/services/GroupsService/Types';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { dateFormatRange } from '@omni/kit/utilities/dateFormatRange';
import { getGroupEventFrequencyFromRRule } from '@omni/kit/utilities/groupUtilities';
import React, { ReactElement } from 'react';
import { View } from 'react-native';

export default function ShareEventConfirmModal({
  isVisible,
  onClose,
  confirmAction,
  title,
  subtitle,
  event,
}: {
  isVisible: boolean;
  onClose: () => void;
  confirmAction: () => void;
  title: string;
  subtitle?: string;
  event: IGroupEvent;
}): ReactElement | null {
  const { sizeClass } = useSizeClass();
  const { t } = useTranslation();

  // @ts-ignore
  const repetitionRules = event?.repetition_rules;

  const onConfirmShare = () => {
    confirmAction();
  };

  return (
    <KitModalV2
      isVisible={isVisible}
      onClose={onClose}
      anchorBottom={sizeClass === SizeClass.Small}
      coverScreen
    >
      <View
        style={{
          width: '100%',
        }}
      >
        <KitText
          black
          extraBold
          fontSize={22}
          style={{ marginBottom: Spacing.s }}
        >
          {title}
        </KitText>
        <KitText gray fontSize={16} style={{ marginBottom: Spacing.l }}>
          {subtitle}
        </KitText>

        <View
          style={{
            width: '100%',
            height: 94,
            borderTopColor: Colors.N100,
            borderTopWidth: 1,
            borderBottomColor: Colors.N100,
            borderBottomWidth: 1,
            marginBottom: Spacing.l,
            justifyContent: 'center',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
            }}
          >
            <KitEventIcon event={event as IEvent} />
            <View style={{ marginLeft: Spacing.m }}>
              <KitText fontSize={16} lineHeight={22} color={Colors.N900}>
                {event.title}
              </KitText>
              <KitText fontSize={14} color={Colors.N500}>
                {dateFormatRange({
                  startDate: event.start_at ?? '',
                  endDate: event.end_at,
                  timezone: event.timezone,
                })}
              </KitText>
              {repetitionRules ? (
                <KitText fontSize={14} color={Colors.N500}>
                  {getGroupEventFrequencyFromRRule(repetitionRules)}
                </KitText>
              ) : null}
            </View>
          </View>
        </View>

        <View
          style={{
            ...(sizeClass === SizeClass.Small && {
              flexDirection: 'column',
            }),
            ...(sizeClass !== SizeClass.Small && {
              flexDirection: 'row-reverse',
              marginTop: Spacing.l,
            }),
          }}
        >
          <KitButton
            color={Colors.N900}
            title={t('common:buttonTitleYesShare')}
            style={{
              ...(sizeClass === SizeClass.Small && { marginTop: Spacing.l }),
            }}
            onPress={onConfirmShare}
            small={sizeClass !== SizeClass.Small}
          />
          <KitButton
            secondary
            color={Colors.N100}
            title={t('common:buttonTitleNoCancel')}
            onPress={onClose}
            style={{
              ...(sizeClass === SizeClass.Small && { marginTop: Spacing.l }),
              ...(sizeClass !== SizeClass.Small && { marginRight: Spacing.m }),
            }}
            small={sizeClass !== SizeClass.Small}
          />
        </View>
      </View>
    </KitModalV2>
  );
}
